// src/pages/Admin/correspondencia/NuevaCorrespondenciaModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import { 
    X, Package, Camera, Search, User, Phone, CreditCard, 
    FileText, Image as ImageIcon, CheckCircle2, MessageSquare, 
    Printer, Building2, UserCheck, AlertCircle, RefreshCw, Send,
    Upload, Trash2, ArrowRight, Copy, Check, ExternalLink, Link2,
    Briefcase, ShieldCheck, AlertTriangle
} from 'lucide-react';
import { 
    createCorrespondencia, 
    Correspondencia, 
    CorrespondenciaCreate,
    fetchClientesElegiblesCorrespondencia,
    ClienteElegibleCorrespondencia
} from '../../../services/correspondencia';
import { fetchUsuarios, CustomUser } from '../../../services/userService';
import { convertImageToWebP } from '../../../utils/imageCompressor';

interface NuevaCorrespondenciaModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    clienteInicial?: CustomUser | null;
}

const NuevaCorrespondenciaModal: React.FC<NuevaCorrespondenciaModalProps> = ({
    isOpen,
    onClose,
    onSave,
    clienteInicial
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // === SECCIÓN 1: PROPIETARIO ===
    const [tipoPropietario, setTipoPropietario] = useState<'registrado' | 'externo'>('registrado');
    const [usuarios, setUsuarios] = useState<CustomUser[]>([]);
    const [clientesElegibles, setClientesElegibles] = useState<ClienteElegibleCorrespondencia[]>([]);
    const [busquedaUsuario, setBusquedaUsuario] = useState('');
    const [usuarioSeleccionado, setUsuarioSeleccionado] = useState<CustomUser | null>(null);
    const [elegibleSeleccionado, setElegibleSeleccionado] = useState<ClienteElegibleCorrespondencia | null>(null);
    const [tipoVinculacion, setTipoVinculacion] = useState<'domiciliacion' | 'cita_servicio' | 'ninguna'>('ninguna');
    const [afiliacionId, setAfiliacionId] = useState<number | null>(null);
    const [citaCorrespondenciaId, setCitaCorrespondenciaId] = useState<number | null>(null);
    const [loadingUsuarios, setLoadingUsuarios] = useState(false);
    const [propNombre, setPropNombre] = useState('');
    const [propApellido, setPropApellido] = useState('');
    const [propTelefono, setPropTelefono] = useState('');
    const [propEmail, setPropEmail] = useState('');
    const [propCiNit, setPropCiNit] = useState('');

    // === SECCIÓN 2: MENSAJERO ===
    const [propietarioDejaPersonalmente, setPropietarioDejaPersonalmente] = useState(true);
    const [mensajeroNombre, setMensajeroNombre] = useState('');
    const [mensajeroApellido, setMensajeroApellido] = useState('');
    const [mensajeroTelefono, setMensajeroTelefono] = useState('');
    const [mensajeroCiNit, setMensajeroCiNit] = useState('');

    // === SECCIÓN 3: DESTINATARIO ===
    const [destNombre, setDestNombre] = useState('');
    const [destTelefono, setDestTelefono] = useState('');
    const [destEmail, setDestEmail] = useState('');
    const [destCiNit, setDestCiNit] = useState('');

    // === SECCIÓN 4: PAQUETE ===
    const [descripcion, setDescripcion] = useState('');
    const [imagenFile, setImagenFile] = useState<File | null>(null);
    const [imagenPreview, setImagenPreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [modoCamaraEnVivo, setModoCamaraEnVivo] = useState(false);
    const [camaraError, setCamaraError] = useState<string | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);

    // Estado post-creación
    const [creadaExitosa, setCreadaExitosa] = useState<Correspondencia | null>(null);
    const [copiadoLink, setCopiadoLink] = useState(false);
    const [copiadoPin, setCopiadoPin] = useState(false);

    // ===== CÁMARA =====
    const stopCameraStream = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    const handleClose = () => {
        stopCameraStream();
        setModoCamaraEnVivo(false);
        setCamaraError(null);
        onClose();
    };

    const startLiveCamera = async () => {
        setCamaraError(null);
        setModoCamaraEnVivo(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
                audio: false
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.play();
            }
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setModoCamaraEnVivo(false);
            setCamaraError('No se pudo acceder a la cámara. Revisa los permisos o selecciona una imagen desde tu dispositivo.');
        }
    };

    const capturePhoto = () => {
        if (!videoRef.current) return;
        const video = videoRef.current;
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 480;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
                if (blob) {
                    const file = new File([blob], `paquete_${Date.now()}.webp`, { type: 'image/webp' });
                    setImagenFile(file);
                    setImagenPreview(canvas.toDataURL('image/webp', 0.88));
                    stopCameraStream();
                    setModoCamaraEnVivo(false);
                    setCamaraError(null);
                }
            }, 'image/webp', 0.88);
        }
    };

    const cancelLiveCamera = () => {
        stopCameraStream();
        setModoCamaraEnVivo(false);
        setCamaraError(null);
    };

    const handleRemovePhoto = () => {
        setImagenFile(null);
        setImagenPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // ===== LIFECYCLE =====
    useEffect(() => {
        if (isOpen) {
            loadUsers();
            setTipoPropietario('registrado');
            setBusquedaUsuario('');
            setUsuarioSeleccionado(null);
            setPropNombre(''); setPropApellido(''); setPropTelefono(''); setPropEmail(''); setPropCiNit('');
            setPropietarioDejaPersonalmente(true);
            setMensajeroNombre(''); setMensajeroApellido(''); setMensajeroTelefono(''); setMensajeroCiNit('');
            setDestNombre(''); setDestTelefono(''); setDestEmail(''); setDestCiNit('');
            setDescripcion(''); setImagenFile(null); setImagenPreview(null);
            setModoCamaraEnVivo(false); setCamaraError(null); setError(null);
            setCreadaExitosa(null); setCopiadoLink(false); setCopiadoPin(false);
        } else {
            stopCameraStream();
            setModoCamaraEnVivo(false);
        }
    }, [isOpen]);

    useEffect(() => { return () => { stopCameraStream(); }; }, []);

    const loadUsers = async () => {
        setLoadingUsuarios(true);
        try {
            const [dataUsers, dataElegibles] = await Promise.all([
                fetchUsuarios(),
                fetchClientesElegiblesCorrespondencia().catch(() => [])
            ]);
            setUsuarios(dataUsers);
            setClientesElegibles(dataElegibles);

            if (clienteInicial) {
                const found = dataUsers.find(u => u.id === clienteInicial.id) || clienteInicial;
                handleSelectUsuarioPropietario(found, dataElegibles);
            }
        } catch (err) {
            /* Browser diagnostics omitted. */
        } finally {
            setLoadingUsuarios(false);
        }
    };

    const handleSelectUsuarioPropietario = (u: CustomUser, elegiblesList?: ClienteElegibleCorrespondencia[]) => {
        setUsuarioSeleccionado(u);
        setPropNombre(u.first_name || '');
        setPropApellido(u.last_name || '');
        setPropTelefono(u.telefono || '');
        setPropEmail(u.email || '');
        setPropCiNit(u.ci_nit || '');

        const list = elegiblesList || clientesElegibles;
        const elegible = list.find(e => e.id === u.id);
        setElegibleSeleccionado(elegible || null);

        if (elegible?.es_domiciliado && elegible.domiciliacion) {
            setTipoVinculacion('domiciliacion');
            setAfiliacionId(elegible.domiciliacion.afiliacion_id);
            setCitaCorrespondenciaId(null);
            setError(null);
        } else if (elegible?.tiene_citas_correspondencia && elegible.citas_correspondencia.length > 0) {
            setTipoVinculacion('cita_servicio');
            setCitaCorrespondenciaId(elegible.citas_correspondencia[0].cita_correspondencia_id);
            setAfiliacionId(null);
            setError(null);
        } else {
            setTipoVinculacion('ninguna');
            setAfiliacionId(null);
            setCitaCorrespondenciaId(null);
        }
    };

    const handleImagenChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const webpFile = await convertImageToWebP(file);
            setImagenFile(webpFile);
            const reader = new FileReader();
            reader.onloadend = () => setImagenPreview(reader.result as string);
            reader.readAsDataURL(webpFile);
            setCamaraError(null);
        }
    };

    const usuariosFiltrados = usuarios.filter(u => {
        if (!busquedaUsuario.trim()) return true;
        const term = busquedaUsuario.toLowerCase();
        const fullName = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
        return (
            fullName.includes(term) ||
            u.email.toLowerCase().includes(term) ||
            (u.ci_nit && u.ci_nit.toLowerCase().includes(term)) ||
            (u.telefono && u.telefono.includes(term))
        );
    });

    // Remitente derivado
    const getRemitenteNombre = () => propietarioDejaPersonalmente ? propNombre.trim() : mensajeroNombre.trim();
    const getRemitenteApellido = () => propietarioDejaPersonalmente ? propApellido.trim() : mensajeroApellido.trim();
    const getRemitenteTelefono = () => propietarioDejaPersonalmente ? propTelefono.trim() : mensajeroTelefono.trim();
    const getRemitenteCiNit = () => propietarioDejaPersonalmente ? propCiNit.trim() : mensajeroCiNit.trim();

    // ===== SUBMIT =====
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (tipoPropietario === 'registrado') {
            if (!usuarioSeleccionado) {
                setError('Debes seleccionar un cliente registrado como propietario del paquete.');
                return;
            }
            if (tipoVinculacion === 'ninguna' && !elegibleSeleccionado?.es_domiciliado && !elegibleSeleccionado?.tiene_citas_correspondencia) {
                setError('El cliente seleccionado no cuenta con un Plan de Domiciliación activo ni un Servicio con correspondencia habilitada.');
                return;
            }
        } else {
            if (!propNombre.trim()) {
                setError('El nombre del propietario es obligatorio.');
                return;
            }
        }

        // Sección 2 (mensajero) y Sección 3 (destinatario) son opcionales
        // No siempre se sabe quién dejará o recogerá el paquete hasta que la persona llegue

        setIsSubmitting(true);
        try {
            const payload: CorrespondenciaCreate = {
                cliente: tipoPropietario === 'registrado' && usuarioSeleccionado ? usuarioSeleccionado.id : null,
                afiliacion: tipoPropietario === 'registrado' && tipoVinculacion === 'domiciliacion' ? afiliacionId : null,
                cita_correspondencia: tipoPropietario === 'registrado' && tipoVinculacion === 'cita_servicio' ? citaCorrespondenciaId : null,
                remitente_nombre: getRemitenteNombre(),
                remitente_apellido: getRemitenteApellido() || undefined,
                remitente_telefono: getRemitenteTelefono() || undefined,
                remitente_ci_nit: getRemitenteCiNit() || undefined,
                destinatario_nombre: destNombre.trim(),
                destinatario_telefono: destTelefono.trim() || undefined,
                destinatario_email: destEmail.trim() || undefined,
                destinatario_ci_nit: destCiNit.trim() || undefined,
                descripcion: descripcion.trim() || undefined,
                imagen: imagenFile,
            };

            const created = await createCorrespondencia(payload);
            setCreadaExitosa(created);
            onSave();
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            const msg = err?.response?.data;
            if (typeof msg === 'object') {
                const firstKey = Object.keys(msg)[0];
                setError(`${firstKey}: ${Array.isArray(msg[firstKey]) ? msg[firstKey][0] : msg[firstKey]}`);
            } else {
                setError('Error al registrar la correspondencia. Intenta nuevamente.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // ===== WHATSAPP / TRACKING =====
    const getTrackingUrl = () => {
        if (!creadaExitosa) return '';
        return `${window.location.origin}/seguimiento/${encodeURIComponent(creadaExitosa.token_verificacion || "")}`;
    };

    const generarLinkWhatsAppDestinatario = () => {
        if (!creadaExitosa) return '#';
        const telefono = creadaExitosa.destinatario_telefono || destTelefono || '';
        const nombreDest = creadaExitosa.destinatario_nombre || destNombre || 'Estimado(a)';
        const digits = (telefono || '').replace(/\D/g, '');
        const finalPhone = digits.length === 8 ? `591${digits}` : digits;
        const trackingUrl = getTrackingUrl();
        const remitenteFull = `${creadaExitosa.remitente_nombre} ${creadaExitosa.remitente_apellido || ''}`.trim();
        const mensaje = `Hola *${nombreDest}*, te informamos que tienes un paquete listo para recoger en recepción de *Comarca Coworking*, dejado por *${remitenteFull}*.\n\n` +
            `📦 *Código de Paquete:* ${creadaExitosa.codigo || `#${creadaExitosa.id}`}\n` +
            `🔐 *PIN de Retiro:* ${creadaExitosa.pin_seguridad}\n\n` +
            `📱 *Puedes ver tu Comprobante y Código QR de retiro aquí:*\n${trackingUrl}\n\n` +
            `_Presenta tu Código QR o dicta tu PIN en recepción al momento de recoger._`;
        return `https://api.whatsapp.com/send?phone=${finalPhone}&text=${encodeURIComponent(mensaje)}`;
    };

    const generarLinkWhatsAppPropietario = () => {
        if (!creadaExitosa) return '#';
        const telefono = propTelefono || '';
        const propFull = `${propNombre} ${propApellido}`.trim() || 'Estimado(a)';
        const nombreDest = creadaExitosa.destinatario_nombre || destNombre || 'el destinatario';
        const digits = (telefono || '').replace(/\D/g, '');
        const finalPhone = digits.length === 8 ? `591${digits}` : digits;
        const trackingUrl = getTrackingUrl();
        const mensaje = `Hola *${propFull}*, confirmamos que hemos recibido y registrado en custodia tu paquete en *Comarca Coworking* para *${nombreDest}*.\n\n` +
            `📦 *Código de Paquete:* ${creadaExitosa.codigo || `#${creadaExitosa.id}`}\n` +
            `🔐 *PIN de Retiro:* ${creadaExitosa.pin_seguridad}\n\n` +
            `📱 *Comprobante y Seguimiento en vivo:*\n${trackingUrl}\n\n` +
            `_Te notificaremos cuando el paquete haya sido entregado. Puedes reenviarle el PIN o este enlace a ${nombreDest}._`;
        return `https://api.whatsapp.com/send?phone=${finalPhone}&text=${encodeURIComponent(mensaje)}`;
    };

    const handleCopiarLink = () => {
        const url = getTrackingUrl();
        if (url) { navigator.clipboard.writeText(url); setCopiadoLink(true); setTimeout(() => setCopiadoLink(false), 2000); }
    };

    const handleCopiarPin = () => {
        if (creadaExitosa?.pin_seguridad) { navigator.clipboard.writeText(creadaExitosa.pin_seguridad); setCopiadoPin(true); setTimeout(() => setCopiadoPin(false), 2000); }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4 backdrop-blur-sm font-['DM_Sans',sans-serif]">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto custom-scroll animate-scale-up">
                {/* Header */}
                <div className="sticky top-0 bg-white z-10 px-6 pt-6 pb-4 border-b border-gray-100 rounded-t-3xl">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-gradient-to-tr from-[#61257b] to-[#8b3fa8] text-white rounded-2xl shadow-sm">
                                <Package className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-gray-800">
                                    {creadaExitosa ? '¡Paquete Registrado!' : 'Recepción de Paquete / Correspondencia'}
                                </h3>
                                <p className="text-xs text-gray-500">
                                    {creadaExitosa ? 'Envía el comprobante por WhatsApp al propietario o destinatario' : 'Registra un paquete dejado en recepción para posterior retiro'}
                                </p>
                            </div>
                        </div>
                        <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* ═══ VISTA 1: ÉXITO ═══ */}
                {creadaExitosa ? (
                    <div className="p-6 space-y-6 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full">
                            <CheckCircle2 className="w-10 h-10" />
                        </div>
                        <div>
                            <span className="px-3 py-1 bg-blue-100 text-[#61257b] font-mono font-bold text-xs rounded-full uppercase tracking-wider">
                                {creadaExitosa.codigo || `#${creadaExitosa.id}`}
                            </span>
                            <h4 className="text-2xl font-black text-gray-900 mt-2">Paquete Guardado en Custodia</h4>
                            <p className="text-xs text-gray-500 mt-1">
                                Propietario: <span className="font-bold text-gray-800">{propNombre} {propApellido}</span>
                                {!propietarioDejaPersonalmente && (
                                    <> • Dejado por: <span className="font-bold text-amber-700">{mensajeroNombre} {mensajeroApellido}</span></>
                                )}
                                &nbsp;• Retira: <span className="font-bold text-[#61257b]">{creadaExitosa.destinatario_nombre || destNombre}</span>
                            </p>
                        </div>

                        {/* PIN Secreto */}
                        <div className="p-4 bg-gradient-to-r from-blue-50 to-teal-50 border-2 border-[#61257b]/30 rounded-2xl max-w-sm mx-auto">
                            <span className="text-xs text-gray-500 uppercase tracking-wider font-bold block mb-1">PIN de Retiro para Recoger el Paquete</span>
                            <div className="flex items-center justify-center gap-2">
                                <span className="text-3xl font-mono font-black text-[#61257b] tracking-widest">{creadaExitosa.pin_seguridad}</span>
                                <button type="button" onClick={handleCopiarPin} className="p-1.5 hover:bg-blue-100 rounded-lg text-[#61257b] transition-colors" title="Copiar PIN">
                                    {copiadoPin ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        {/* Link de seguimiento público */}
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-2xl max-w-md mx-auto">
                            <span className="text-[10px] text-gray-500 uppercase tracking-wider font-bold block mb-1.5">🔗 Enlace de Seguimiento Público (sin login)</span>
                            <div className="flex items-center gap-2">
                                <input type="text" readOnly value={getTrackingUrl()} className="flex-1 px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg text-[11px] text-gray-700 font-mono outline-none" />
                                <button type="button" onClick={handleCopiarLink} className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-[#61257b] bg-white hover:bg-blue-50 border border-blue-200 rounded-lg shadow-sm transition-colors">
                                    {copiadoLink ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                                    <span>{copiadoLink ? '¡Copiado!' : 'Copiar'}</span>
                                </button>
                            </div>
                        </div>

                        {/* WhatsApp */}
                        <div className="space-y-3 max-w-md mx-auto text-left">
                            <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider text-center">¿A quién deseas enviar la notificación por WhatsApp?</label>
                            <a href={generarLinkWhatsAppDestinatario()} target="_blank" rel="noopener noreferrer"
                                className="w-full py-3.5 px-4 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold text-sm rounded-2xl shadow-md hover:shadow-lg transition-all flex items-center justify-between group">
                                <div className="flex items-center gap-2.5">
                                    <MessageSquare className="w-5 h-5 fill-white" />
                                    <div>
                                        <div className="text-sm font-bold">Enviar al Destinatario (Quién recogerá)</div>
                                        <div className="text-[11px] font-normal text-white/90">{creadaExitosa.destinatario_nombre || destNombre} {destTelefono ? `(${destTelefono})` : ''}</div>
                                    </div>
                                </div>
                                <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </a>
                            <a href={generarLinkWhatsAppPropietario()} target="_blank" rel="noopener noreferrer"
                                className="w-full py-3.5 px-4 bg-[#61257b] hover:bg-[#006ba8] text-white font-bold text-sm rounded-2xl shadow-md hover:shadow-lg transition-all flex items-center justify-between group">
                                <div className="flex items-center gap-2.5">
                                    <MessageSquare className="w-5 h-5 fill-white" />
                                    <div>
                                        <div className="text-sm font-bold">Enviar Comprobante al Propietario</div>
                                        <div className="text-[11px] font-normal text-white/90">{propNombre} {propApellido} {propTelefono ? `(${propTelefono})` : ''}</div>
                                    </div>
                                </div>
                                <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </a>
                            <div className="flex gap-3 pt-2">
                                <button onClick={() => window.print()} className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-xs rounded-xl flex items-center justify-center gap-2 transition-colors">
                                    <Printer className="w-4 h-4" /> Imprimir Ticket
                                </button>
                                <button onClick={onClose} className="flex-1 py-3 px-4 bg-gray-800 hover:bg-black text-white font-bold text-xs rounded-xl hover:shadow-md transition-all">
                                    Finalizar y Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    /* ═══ VISTA 2: FORMULARIO ═══ */
                    <form onSubmit={handleSubmit} className="p-6 space-y-5">
                        {error && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs flex items-center gap-2">
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        {/* ══════ SECCIÓN 1: PROPIETARIO ══════ */}
                        <div className="space-y-3">
                            <label className="block text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                                <User className="w-4 h-4 text-[#61257b]" />
                                1. Propietario del Paquete <span className="text-red-500">*</span>
                                <span className="text-[10px] text-gray-400 font-normal lowercase ml-1">(dueño de la encomienda)</span>
                            </label>
                            <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-2xl">
                                <button type="button" onClick={() => { setTipoPropietario('registrado'); setError(null); }}
                                    className={`py-2.5 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${tipoPropietario === 'registrado' ? 'bg-white text-[#61257b] shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>
                                    <UserCheck className="w-4 h-4" /> Cliente Registrado
                                </button>
                                <button type="button" onClick={() => { setTipoPropietario('externo'); setUsuarioSeleccionado(null); setPropNombre(''); setPropApellido(''); setPropTelefono(''); setPropEmail(''); setPropCiNit(''); setError(null); }}
                                    className={`py-2.5 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${tipoPropietario === 'externo' ? 'bg-white text-[#61257b] shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>
                                    <Building2 className="w-4 h-4" /> Persona Externa / Sin Cuenta
                                </button>
                            </div>

                            {tipoPropietario === 'registrado' ? (
                                <div className="space-y-2 p-3.5 bg-blue-50/60 border border-blue-200/80 rounded-2xl">
                                    <span className="text-xs text-blue-900 font-semibold block">Buscar Cliente Registrado (propietario del paquete):</span>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input type="text" value={busquedaUsuario} onChange={(e) => setBusquedaUsuario(e.target.value)}
                                            placeholder="Buscar por nombre, correo o CI..."
                                            className="pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-xl w-full text-xs focus:ring-2 focus:ring-[#61257b]/20 focus:border-[#61257b] outline-none" />
                                    </div>
                                    {!usuarioSeleccionado && (
                                        <div className="max-h-48 overflow-y-auto space-y-1 custom-scroll bg-white rounded-xl border border-gray-200 p-1">
                                            {loadingUsuarios ? (
                                                <p className="text-center py-3 text-xs text-gray-400">Cargando clientes y planes...</p>
                                            ) : usuariosFiltrados.length === 0 ? (
                                                <p className="text-center py-3 text-xs text-gray-400">No se encontraron clientes registrados.</p>
                                            ) : (
                                                usuariosFiltrados.slice(0, 15).map(u => {
                                                    const elegible = clientesElegibles.find(e => e.id === u.id);
                                                    return (
                                                        <div key={u.id} onClick={() => handleSelectUsuarioPropietario(u)}
                                                            className="p-2.5 rounded-lg text-xs cursor-pointer flex items-center justify-between transition-colors hover:bg-gray-50 border border-transparent hover:border-blue-100 text-gray-700">
                                                            <div>
                                                                <div className="flex items-center gap-1.5 font-semibold text-gray-900">
                                                                    <span>{u.first_name} {u.last_name}</span>
                                                                    {elegible?.es_domiciliado && (
                                                                        <span className="px-1.5 py-0.5 bg-purple-100 text-purple-800 text-[10px] font-bold rounded-full border border-purple-200 flex items-center gap-1">
                                                                            <Building2 className="w-3 h-3" /> Domiciliado ({elegible.domiciliacion?.empresa_nombre})
                                                                        </span>
                                                                    )}
                                                                    {elegible?.tiene_citas_correspondencia && !elegible.es_domiciliado && (
                                                                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-bold rounded-full border border-blue-200 flex items-center gap-1">
                                                                            <Package className="w-3 h-3" /> Servicio Activo
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <span className="block text-[10px] text-gray-400 mt-0.5">
                                                                    {u.email} {u.ci_nit ? `• CI: ${u.ci_nit}` : ''} {u.telefono ? `• Tel: ${u.telefono}` : ''}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    )}
                                    {usuarioSeleccionado && (
                                        <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-2xl space-y-3 animate-fade-in">
                                            <div className="flex items-center justify-between text-xs text-emerald-900 font-bold border-b border-emerald-200/60 pb-1.5">
                                                <span className="flex items-center gap-1.5">
                                                    <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Propietario Seleccionado:
                                                </span>
                                                <button type="button" onClick={() => { setUsuarioSeleccionado(null); setElegibleSeleccionado(null); setPropNombre(''); setPropApellido(''); setPropTelefono(''); setPropEmail(''); setPropCiNit(''); setTipoVinculacion('ninguna'); }}
                                                    className="text-emerald-700 hover:text-emerald-900 text-xs underline font-semibold">Cambiar cliente</button>
                                            </div>

                                            {/* Badge de Vinculación de Plan / Servicio */}
                                            {elegibleSeleccionado?.es_domiciliado ? (
                                                <div className="p-2.5 bg-purple-50 border border-purple-200 rounded-xl">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-7 h-7 rounded-lg bg-purple-600 text-white flex items-center justify-center font-bold text-xs">
                                                                <Building2 className="w-4 h-4" />
                                                            </div>
                                                            <div>
                                                                <span className="text-[10px] text-purple-700 uppercase font-bold tracking-wider block">Plan Exclusivo de Domiciliación Activo</span>
                                                                <span className="text-xs font-black text-purple-950">{elegibleSeleccionado.domiciliacion?.empresa_nombre}</span>
                                                                {elegibleSeleccionado.domiciliacion?.nit && (
                                                                    <span className="text-[11px] text-purple-700 ml-1.5 font-mono">NIT: {elegibleSeleccionado.domiciliacion.nit}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <span className="px-2 py-0.5 bg-purple-600 text-white text-[10px] font-bold rounded-full">
                                                            Vinculado a Empresa
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : elegibleSeleccionado?.tiene_citas_correspondencia && elegibleSeleccionado.citas_correspondencia.length > 0 ? (
                                                <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-xl">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-7 h-7 rounded-lg bg-[#61257b] text-white flex items-center justify-center font-bold text-xs">
                                                                <Package className="w-4 h-4" />
                                                            </div>
                                                            <div>
                                                                <span className="text-[10px] text-blue-700 uppercase font-bold tracking-wider block">Servicio con Correspondencia Habilitada</span>
                                                                <span className="text-xs font-black text-blue-950">
                                                                    {elegibleSeleccionado.citas_correspondencia[0].servicio_nombre || 'Servicio'} (Cita #{elegibleSeleccionado.citas_correspondencia[0].cita_codigo})
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <span className="px-2 py-0.5 bg-[#61257b] text-white text-[10px] font-bold rounded-full">
                                                            Vinculado a Servicio
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="p-2.5 bg-amber-50 border border-amber-300 rounded-xl text-amber-900 text-xs flex items-center gap-2">
                                                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                                    <span>Este usuario no tiene un Plan de Domiciliación ni un servicio con correspondencia activo. Para recibir paquetes debe afiliarse o contratar un servicio con correspondencia.</span>
                                                </div>
                                            )}

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                                <div>
                                                    <label className="text-[10px] text-gray-500 block font-medium">Nombre / Titular:</label>
                                                    <p className="px-2.5 py-1.5 bg-white border border-emerald-300 rounded-lg text-xs font-bold text-gray-800">{propNombre} {propApellido}</p>
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-gray-500 block font-medium">Correo:</label>
                                                    <p className="px-2.5 py-1.5 bg-white border border-emerald-300 rounded-lg text-xs text-gray-800">{propEmail || '—'}</p>
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-gray-500 block font-medium">Teléfono:</label>
                                                    <p className="px-2.5 py-1.5 bg-white border border-emerald-300 rounded-lg text-xs text-gray-800">{propTelefono || '—'}</p>
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-gray-500 block font-medium">CI / NIT:</label>
                                                    <p className="px-2.5 py-1.5 bg-white border border-emerald-300 rounded-lg text-xs text-gray-800">{propCiNit || '—'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="p-3.5 bg-gray-50 border border-gray-200 rounded-2xl space-y-3">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Nombre del Propietario <span className="text-red-500">*</span></label>
                                            <input type="text" value={propNombre} onChange={(e) => setPropNombre(e.target.value)} placeholder="Ej: Carlos Gómez"
                                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#61257b]/20 focus:border-[#61257b] outline-none" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Apellido (Opcional)</label>
                                            <input type="text" value={propApellido} onChange={(e) => setPropApellido(e.target.value)} placeholder="Apellido"
                                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#61257b]/20 focus:border-[#61257b] outline-none" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1"><Phone className="w-3 h-3 text-emerald-600" /> Celular / WhatsApp</label>
                                            <input type="text" value={propTelefono} onChange={(e) => setPropTelefono(e.target.value)} placeholder="70012345"
                                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#61257b]/20 focus:border-[#61257b] outline-none" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Correo Electrónico (Opcional)</label>
                                            <input type="email" value={propEmail} onChange={(e) => setPropEmail(e.target.value)} placeholder="correo@ejemplo.com"
                                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#61257b]/20 focus:border-[#61257b] outline-none" />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-medium text-gray-700 mb-1">CI / NIT (Opcional)</label>
                                            <input type="text" value={propCiNit} onChange={(e) => setPropCiNit(e.target.value)} placeholder="Carnet o NIT"
                                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#61257b]/20 focus:border-[#61257b] outline-none" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ══════ SECCIÓN 2: MENSAJERO ══════ */}
                        <div className="space-y-3 pt-2 border-t border-gray-100">
                            <label className="block text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                                <ArrowRight className="w-4 h-4 text-amber-600" />
                                2. ¿Quién deja el paquete? <span className="text-[10px] text-gray-400 font-normal lowercase ml-1">(opcional)</span>
                            </label>
                            <div className={`p-3 rounded-2xl border cursor-pointer transition-all ${propietarioDejaPersonalmente ? 'bg-emerald-50 border-emerald-300' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                                onClick={() => setPropietarioDejaPersonalmente(true)}>
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${propietarioDejaPersonalmente ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300'}`}>
                                        {propietarioDejaPersonalmente && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                    <div>
                                        <span className="text-xs font-bold text-gray-800 block">El propietario lo deja personalmente</span>
                                        <span className="text-[10px] text-gray-500">
                                            {propNombre ? `${propNombre} ${propApellido} entrega el paquete` : 'Los datos del remitente serán los del propietario seleccionado'}
                                        </span>
                                    </div>
                                </label>
                            </div>
                            <div className={`p-3 rounded-2xl border cursor-pointer transition-all ${!propietarioDejaPersonalmente ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                                onClick={() => setPropietarioDejaPersonalmente(false)}>
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${!propietarioDejaPersonalmente ? 'border-amber-500 bg-amber-500' : 'border-gray-300'}`}>
                                        {!propietarioDejaPersonalmente && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                    <div>
                                        <span className="text-xs font-bold text-gray-800 block">Otra persona / mensajero lo deja</span>
                                        <span className="text-[10px] text-gray-500">El propietario envía a alguien más a dejar la encomienda</span>
                                    </div>
                                </label>
                            </div>

                            {!propietarioDejaPersonalmente && (
                                <div className="p-3.5 bg-amber-50/60 border border-amber-200 rounded-2xl space-y-3 animate-fade-in">
                                    <span className="text-xs text-amber-900 font-semibold block flex items-center gap-1.5">
                                        <User className="w-3.5 h-3.5" /> Datos de la persona que deja el paquete (mensajero) - Opcional:
                                    </span>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Nombre (Opcional)</label>
                                            <input type="text" value={mensajeroNombre} onChange={(e) => setMensajeroNombre(e.target.value)} placeholder="Nombre del mensajero"
                                                className="w-full px-3 py-2 bg-white border border-amber-300 rounded-xl text-xs focus:ring-2 focus:ring-amber-300/30 focus:border-amber-400 outline-none" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Apellido (Opcional)</label>
                                            <input type="text" value={mensajeroApellido} onChange={(e) => setMensajeroApellido(e.target.value)} placeholder="Apellido"
                                                className="w-full px-3 py-2 bg-white border border-amber-300 rounded-xl text-xs focus:ring-2 focus:ring-amber-300/30 focus:border-amber-400 outline-none" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Teléfono (Opcional)</label>
                                            <input type="text" value={mensajeroTelefono} onChange={(e) => setMensajeroTelefono(e.target.value)} placeholder="Teléfono del mensajero"
                                                className="w-full px-3 py-2 bg-white border border-amber-300 rounded-xl text-xs focus:ring-2 focus:ring-amber-300/30 focus:border-amber-400 outline-none" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">CI / NIT (Opcional)</label>
                                            <input type="text" value={mensajeroCiNit} onChange={(e) => setMensajeroCiNit(e.target.value)} placeholder="Carnet o NIT"
                                                className="w-full px-3 py-2 bg-white border border-amber-300 rounded-xl text-xs focus:ring-2 focus:ring-amber-300/30 focus:border-amber-400 outline-none" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ══════ SECCIÓN 3: DESTINATARIO ══════ */}
                        <div className="space-y-3 pt-2 border-t border-gray-100">
                            <div className="flex items-center gap-2 text-xs font-bold text-gray-700 uppercase tracking-wider">
                                <Send className="w-4 h-4 text-[#61257b]" />
                                3. Destinatario (Persona que retirará el paquete) <span className="text-[10px] text-gray-400 font-normal lowercase ml-1">(opcional)</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3.5 bg-gray-50 border border-gray-200 rounded-2xl">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Nombre Completo del Destinatario (Opcional)</label>
                                    <input type="text" value={destNombre} onChange={(e) => setDestNombre(e.target.value)} placeholder="Nombre de quien retirará el paquete"
                                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#61257b]/20 focus:border-[#61257b] outline-none" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                                        <MessageSquare className="w-3.5 h-3.5 text-emerald-600" /> Celular / WhatsApp (para enviarle el QR y PIN)
                                    </label>
                                    <input type="text" value={destTelefono} onChange={(e) => setDestTelefono(e.target.value)} placeholder="Ej: 70012345"
                                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#61257b]/20 focus:border-[#61257b] outline-none" />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">CI / NIT Destinatario (Opcional)</label>
                                    <input type="text" value={destCiNit} onChange={(e) => setDestCiNit(e.target.value)} placeholder="Carnet para validación"
                                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#61257b]/20 focus:border-[#61257b] outline-none" />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Correo Electrónico (Opcional)</label>
                                    <input type="email" value={destEmail} onChange={(e) => setDestEmail(e.target.value)} placeholder="correo@ejemplo.com"
                                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#61257b]/20 focus:border-[#61257b] outline-none" />
                                </div>
                            </div>
                        </div>

                        {/* ══════ SECCIÓN 4: DESCRIPCIÓN + FOTO ══════ */}
                        <div className="space-y-3 pt-2 border-t border-gray-100">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                                    <FileText className="w-3.5 h-3.5 text-[#61257b]" /> 4. Descripción del Paquete o Documentos
                                </label>
                                <textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={2}
                                    placeholder="Ej: Sobre manila con contratos, caja pequeña de Amazon, paquete sellado..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#61257b]/20 focus:border-[#61257b] outline-none resize-none" />
                            </div>

                            <div className="space-y-2 pt-1 border-t border-gray-100">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                                        <Camera className="w-3.5 h-3.5 text-[#61257b]" />
                                        5. Foto del Paquete <span className="text-[10px] text-gray-400 font-normal lowercase">(opcional)</span>
                                    </label>
                                    {imagenPreview && !modoCamaraEnVivo && (
                                        <button type="button" onClick={handleRemovePhoto} className="text-[11px] text-red-500 hover:text-red-700 flex items-center gap-1 font-semibold transition-colors">
                                            <Trash2 className="w-3 h-3" /> Eliminar foto
                                        </button>
                                    )}
                                </div>

                                {modoCamaraEnVivo && (
                                    <div className="relative rounded-2xl overflow-hidden bg-black aspect-video flex items-center justify-center border-2 border-[#8b3fa8] shadow-inner">
                                        <video ref={videoRef} className="w-full h-full object-cover" autoPlay playsInline muted />
                                        <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm text-white px-2.5 py-1 rounded-full text-[11px] flex items-center gap-1.5 font-medium shadow">
                                            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span> Cámara en vivo
                                        </div>
                                        <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-3 px-4">
                                            <button type="button" onClick={capturePhoto} className="px-5 py-2.5 bg-gradient-to-r from-[#61257b] to-[#8b3fa8] text-white rounded-xl text-xs font-bold shadow-lg flex items-center gap-2 hover:scale-105 transition-all">
                                                <Camera className="w-4 h-4" /> Capturar Foto Ahora
                                            </button>
                                            <button type="button" onClick={cancelLiveCamera} className="px-4 py-2.5 bg-black/70 backdrop-blur-sm text-white rounded-xl text-xs font-semibold hover:bg-black/90 transition-colors">
                                                Cancelar
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {!modoCamaraEnVivo && imagenPreview && (
                                    <div className="relative group rounded-2xl overflow-hidden border border-gray-200 bg-gray-50 h-36 flex items-center justify-center">
                                        <img src={imagenPreview} alt="Foto Paquete" className="max-h-full max-w-full object-contain" />
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                            <button type="button" onClick={startLiveCamera} className="px-3 py-1.5 bg-white text-gray-800 text-xs font-semibold rounded-lg hover:bg-gray-100 flex items-center gap-1.5 shadow">
                                                <Camera className="w-3.5 h-3.5 text-[#61257b]" /> Tomar otra foto
                                            </button>
                                            <button type="button" onClick={() => fileInputRef.current?.click()} className="px-3 py-1.5 bg-white text-gray-800 text-xs font-semibold rounded-lg hover:bg-gray-100 flex items-center gap-1.5 shadow">
                                                <Upload className="w-3.5 h-3.5 text-[#61257b]" /> Cambiar archivo
                                            </button>
                                            <button type="button" onClick={handleRemovePhoto} className="px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 flex items-center gap-1 shadow">
                                                <Trash2 className="w-3.5 h-3.5" /> Eliminar
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {!modoCamaraEnVivo && !imagenPreview && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                        <input type="file" ref={fileInputRef} accept="image/*" onChange={handleImagenChange} className="hidden" />
                                        <button type="button" onClick={startLiveCamera}
                                            className="py-3.5 px-3 border-2 border-dashed border-[#61257b]/40 hover:border-[#61257b] bg-blue-50/40 hover:bg-blue-50/80 text-[#61257b] rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all group cursor-pointer">
                                            <Camera className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                            <span className="text-xs font-bold">Tomar con Cámara</span>
                                            <span className="text-[10px] text-gray-500">Activar cámara web en vivo</span>
                                        </button>
                                        <button type="button" onClick={() => fileInputRef.current?.click()}
                                            className="py-3.5 px-3 border-2 border-dashed border-gray-300 hover:border-gray-400 bg-gray-50/60 hover:bg-gray-100 text-gray-700 rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all group cursor-pointer">
                                            <Upload className="w-5 h-5 text-gray-500 group-hover:scale-110 transition-transform" />
                                            <span className="text-xs font-bold">Adjuntar de Equipo</span>
                                            <span className="text-[10px] text-gray-500">Subir imagen JPG o PNG</span>
                                        </button>
                                    </div>
                                )}

                                {camaraError && (
                                    <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2 text-xs text-amber-800 animate-fade-in">
                                        <AlertCircle className="w-4 h-4 flex-shrink-0 text-amber-600" />
                                        <span>{camaraError}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* BOTONES */}
                        <div className="flex gap-3 pt-2">
                            <button type="button" onClick={handleClose} className="flex-1 py-3 px-4 border border-gray-300 rounded-xl text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                                Cancelar
                            </button>
                            <button type="submit" disabled={isSubmitting}
                                className="flex-1 py-3 px-4 bg-gradient-to-r from-[#61257b] to-[#8b3fa8] text-white text-xs font-bold rounded-xl hover:shadow-md transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                                {isSubmitting ? (
                                    <><RefreshCw className="w-4 h-4 animate-spin" /> Registrando...</>
                                ) : (
                                    <><Package className="w-4 h-4" /> Registrar Encomienda</>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default NuevaCorrespondenciaModal;
